/**
 * Warranty terms as pages we draw ourselves.
 *
 * The Buyers Guide's SYSTEMS COVERED box is a form field, and filling it with
 * the full terms depends on the PDF library re-flowing a multiline field,
 * which it does not do reliably. So the field gets a one-line summary and the
 * terms go here, drawn with direct text calls.
 *
 * The layout flows onto a second page automatically. The text currently fits
 * one page with little to spare, and clipped warranty terms would be worse
 * than an extra sheet of paper.
 */

import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from "pdf-lib";
import { termFor } from "../lib/warranty";

const INK = rgb(0.10, 0.13, 0.17);
const SOFT = rgb(0.38, 0.43, 0.48);
const RULE = rgb(0.84, 0.87, 0.90);

const LEFT = 54, RIGHT = 558;
const WIDTH = RIGHT - LEFT;
const TOP = 738;
const BOTTOM = 130;          // leaves room for the signature block
const SIZE = 9.5;
const LEAD = SIZE * 1.3;

/** Short enough for the Buyers Guide field without needing to wrap. */
export function warrantySummary(days: number): string {
  const t = termFor(days);
  return t.days ? `Powertrain — ${t.printed}. Full terms attached.` : "";
}

interface Block { heading: string; paras: string[] }

function blocks(days: number, dealer: string): Block[] {
  const t = termFor(days);
  return [
    {
      heading: "POWERTRAIN WARRANTY",
      paras: [
        "Covers the essential parts of the vehicle's propulsion system, including the " +
        "engine and transmission, and the components that transfer power to the wheels.",
      ],
    },
    {
      heading: "DURATION",
      paras: [`${t.printed} from the date of sale, whichever occurs first.`],
    },
    {
      heading: "COVERED COMPONENTS",
      paras: [
        "This is a limited powertrain warranty. Coverage applies only to the components " +
        "listed below. Any part, system, fluid, or labor not specifically listed is not covered.",
        "Engine. Internal lubricated parts only, including pistons, piston rings, valves, " +
        "valve springs, crankshaft and main bearings, camshaft and bearings, connecting " +
        "rods, oil pump, and water pump. The cylinder block and cylinder heads are covered " +
        "only if damaged by the failure of a listed internal part.",
        "Transmission. Internal parts of automatic and manual transmissions, including the " +
        "torque converter, gears, shafts, and clutches (where applicable). The transmission " +
        "case is covered only if damaged by the failure of a listed internal part.",
        "4WD / AWD. Rear axle, front drive axle, transfer case, and related internal " +
        "components. Axle shaft boots (CV boots) are not covered.",
        "This warranty does not cover seals, gaskets, hoses, belts, sensors, electronics, " +
        "engine or transmission control modules, mounts, flywheels, flexplates, clutches on " +
        "manual transmissions unless listed above, turbochargers, superchargers, or any part " +
        "not named in this section. Seals and gaskets are replaced only when required to " +
        "complete a covered repair.",
        "Coverage is limited to sudden mechanical failure of a listed part. Normal wear, " +
        "lack of maintenance, overheating damage from continued operation, contamination of " +
        "fluids, and pre-existing conditions are not covered.",
      ],
    },
    {
      heading: "WHAT VOIDS THIS WARRANTY",
      paras: [
        "If the vehicle shows any mechanical failure or warning — including overheating, " +
        "low oil pressure, coolant or oil leaks, loss of power, transmission shifting " +
        "problems, unusual noise, or a check engine light — you must immediately and safely " +
        "stop driving it. Continuing to drive after a warning or failure is neglect and " +
        "voids this warranty.",
        `The vehicle must be towed to ${dealer} for inspection and repair. Do not drive it, ` +
        "even a short distance. Towing is not covered under any circumstances. We recommend " +
        "carrying AAA or other roadside assistance on your insurance policy.",
        `All warranty repairs must be performed by ${dealer} unless we authorize another ` +
        "shop in writing before the work begins. Repairs performed elsewhere void this warranty.",
        "This warranty is also void if the vehicle is used for racing, off-road use beyond " +
        "normal street driving, commercial towing or hauling beyond manufacturer ratings, or " +
        "if it has been modified in a way that affects a covered component. Failure to " +
        "maintain proper fluid levels and required service (oil changes, transmission " +
        "service, coolant service) according to the manufacturer's schedule also voids " +
        "coverage for related failures. Keep receipts.",
        `You must notify ${dealer} as soon as a problem appears and before any repair is ` +
        "started. We decide whether a failure is covered after inspection. If the inspection " +
        "shows the problem is not covered, you are responsible for diagnostic and teardown " +
        "charges.",
      ],
    },
  ];
}

function wrap(text: string, font: PDFFont, size: number, width: number): string[] {
  const out: string[] = [];
  let line = "";
  for (const word of text.split(" ")) {
    const t = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(t, size) <= width) line = t;
    else { if (line) out.push(line); line = word; }
  }
  if (line) out.push(line);
  return out;
}

export async function appendWarrantyPage(
  doc: PDFDocument,
  days: number,
  dealerName: string,
  vehicle: { year: number; make: string; model: string; vin: string },
  dealNo: string,
): Promise<void> {
  if (!days) return;

  const reg = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);

  let page: PDFPage = doc.addPage([612, 792]);
  let y = TOP;
  let continued = false;

  const header = () => {
    page.drawText(continued ? "LIMITED POWERTRAIN WARRANTY (CONTINUED)"
                            : "LIMITED POWERTRAIN WARRANTY",
      { x: LEFT, y, size: 15, font: bold, color: INK });
    y -= 16;
    page.drawText(`${dealerName}  ·  Deal ${dealNo}`,
      { x: LEFT, y, size: 9, font: reg, color: SOFT });
    y -= 12;
    page.drawText(`${vehicle.year} ${vehicle.make} ${vehicle.model}  ·  VIN ${vehicle.vin}`,
      { x: LEFT, y, size: 9, font: reg, color: SOFT });
    y -= 14;
    page.drawLine({ start: { x: LEFT, y }, end: { x: RIGHT, y },
                    thickness: 0.9, color: RULE });
    y -= 22;
  };

  /** Start a new sheet rather than let anything run off the bottom. */
  const ensure = (needed: number) => {
    if (y - needed >= BOTTOM) return;
    page = doc.addPage([612, 792]);
    y = TOP;
    continued = true;
    header();
  };

  header();

  for (const b of blocks(days, dealerName)) {
    ensure(14 + LEAD * 2);
    page.drawText(b.heading, { x: LEFT, y, size: SIZE, font: bold, color: INK });
    y -= 14;

    for (const para of b.paras) {
      const lines = wrap(para, reg, SIZE, WIDTH);
      for (const line of lines) {
        ensure(LEAD);
        page.drawText(line, { x: LEFT, y, size: SIZE, font: reg, color: INK });
        y -= LEAD;
      }
      y -= 5;
    }
    y -= 8;
  }

  // Signature block, always on the last sheet.
  const sigY = Math.min(y - 20, 104);
  page.drawLine({ start: { x: LEFT, y: sigY + 30 }, end: { x: RIGHT, y: sigY + 30 },
                  thickness: 0.9, color: RULE });
  page.drawText("This warranty is part of the sale documents for the vehicle above. "
    + "The FTC Buyers Guide governs where it conflicts.",
    { x: LEFT, y: sigY + 16, size: 8, font: reg, color: SOFT });
  page.drawText("Buyer signature", { x: LEFT, y: sigY - 14, size: 8.5, font: reg, color: SOFT });
  page.drawLine({ start: { x: LEFT, y: sigY - 18 }, end: { x: 300, y: sigY - 18 },
                  thickness: 0.7, color: RULE });
  page.drawText("Date", { x: 330, y: sigY - 14, size: 8.5, font: reg, color: SOFT });
  page.drawLine({ start: { x: 330, y: sigY - 18 }, end: { x: RIGHT, y: sigY - 18 },
                  thickness: 0.7, color: RULE });
}
